// ─── STALE-CLIENT DETECTION (pure logic, unit-tested) ───────────────────────
// A client that outlives a deploy holds an index.html whose hashed lazy
// chunks no longer exist on the CDN. These two gates decide (a) whether an
// error is evidence of that staleness, and (b) whether another recovery
// reload is allowed. The side-effectful recovery lives in SixGuysRP.jsx
// (recoverToVersion for the update poll, purgeAndReload for the error
// boundary); the decisions live here so they stay testable against the exact
// strings production has produced.

// Chrome: "Failed to fetch dynamically imported module: https://…"
// Safari: "Importing a module script failed."
// Firefox: "error loading dynamically imported module"
// Vite:   "Unable to preload CSS", webpack-style "ChunkLoadError"/"Loading chunk"
// React.lazy interop on a stale/mismatched chunk:
//   "Cannot read properties of undefined (reading 'default')"
//   — client_errors row 2026-07-30 04:06 UTC, Haley's Mac: a PWA window
//     stranded on v131 lazy-loading a chunk the v135 deploy had deleted.
//     The old classifier missed this spelling, so no auto-recovery ran.
//     Permanent fixture.
const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  'ChunkLoadError',
  'Loading chunk',
  "reading 'default'",
];

// Accepts an Error, an event reason, or a bare string.
export function isChunkLoadError(error) {
  if (!error) return false;
  const m = String(error.message || error || '');
  return CHUNK_ERROR_PATTERNS.some(p => m.includes(p));
}

// Bounded retry counter for the update poll. Returns the attempt number to
// record (1-based), or null when the budget is spent and no further reload
// may happen. `stored` is whatever localStorage held (string, null, garbage).
export function nextReloadAttempt(stored, max = 3) {
  const attempts = parseInt(stored || '0', 10) || 0;
  if (attempts >= max) return null;
  return attempts + 1;
}

// ─── MANUAL HARD RELOAD (the update pill's tap) ─────────────────────────────
// v155: version updates NEVER auto-reload — six same-day deploys were
// force-refreshing both players mid-play ("we keep getting random refreshes").
// A detected update only shows the pill; THIS runs when the user taps it.
// No attempt counters: a deliberate tap is always allowed. Cache-busted
// document request because prod has served `age:`-stamped documents despite
// no-store; the SW is unregistered outright (it has no fetch handler and
// re-registers on boot).
export async function hardReloadToVersion(version) {
  try { navigator.serviceWorker?.controller?.postMessage('CLEAR_CACHES'); } catch { /* best effort */ }
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) || [];
    for (const r of regs) { try { await r.unregister(); } catch { /* best effort */ } }
  } catch { /* best effort */ }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch { /* best effort */ }
  window.location.replace(`/?v=${encodeURIComponent(version || Date.now())}`);
}
